// locationApi.js – thin wrapper around the Namasvi Location backend service.
// Base URL priority:
//   1. REACT_APP_LOCATION_DOMAIN  – dedicated location service (prod/staging)
//   2. REACT_APP_API_DOMAIN       – main API as fallback (dev / local)
// Both are resolved at build time so switching envs (dev/staging/prod) picks
// the right URL automatically without any code change.

import axios from "axios";

const getBase = () => {
  const raw =
    process.env.REACT_APP_LOCATION_DOMAIN ||
    process.env.REACT_APP_API_DOMAIN ||
    "";
  // Strip any accidental trailing slash so URL joins are clean
  return raw.replace(/\/+$/, "");
};

/**
 * Maps a raw result object from the search API into a react-select option.
 * The API may return different shapes depending on the detected_type, so we
 * are deliberately defensive here.
 */
export const toSelectOption = (result) => {
  if (!result) return null;
  if (typeof result === "string") return { value: result, label: result };

  // Check pincode FIRST — a result can have both city + pincode fields;
  // the pincode branch must win so the pincode number appears in the label.
  // API shape: { pincode, city, state, country, place_id }
  // Note: the location API does not return a sub-area/locality name for
  // pincodes — only city-level data is available from the backend.
  if (result.pincode) {
    const parts = [String(result.pincode), result.city, result.state, result.country].filter(Boolean);
    return { value: String(result.pincode), label: parts.join(", ") };
  }

  // Handle city shape: { city, state, country, place_id }
  if (result.city) {
    const parts = [result.city, result.state, result.country].filter(Boolean);
    const label = parts.join(", ");
    return { value: result.city, label };
  }

  // Handle state shape: { state, country, ... }
  if (result.state && !result.city) {
    const label = [result.state, result.country].filter(Boolean).join(", ");
    return { value: result.state, label };
  }

  // Generic fallback for other shapes
  const label =
    result.display_name ||
    result.name ||
    result.label ||
    result.place_name ||
    result.formatted_address ||
    "";
  const value = result.name || result.display_name || label;
  if (!value) return null;
  return { value, label };
};

/**
 * Search location suggestions.
 *
 * @param {string} term  – user input (min 2 chars)
 * @param {"auto"|"pincode"|"city"|"state"|"country"} type
 * @param {number} limit – max results (1-10)
 * @returns {Promise<Array<{value: string, label: string}>>}  react-select options
 */
export const searchLocations = async (term, type = "auto", limit = 10) => {
  const trimmed = (term || "").trim();
  if (trimmed.length < 2) return [];

  const base = getBase();
  if (!base) {
    console.warn("[locationApi] No base URL configured. Set REACT_APP_LOCATION_DOMAIN or REACT_APP_API_DOMAIN.");
    return [];
  }

  try {
    const res = await axios.get(`${base}/api/v2/location/search`, {
      params: { term: trimmed, type, country_code: "IN", limit },
    });

    // API returns { results: [...], detected_type, total_results } directly
    const results =
      res.data?.results ||
      res.data?.data?.results ||
      [];

    return (Array.isArray(results) ? results : [])
      .map(toSelectOption)
      .filter(Boolean)
      // Remove duplicates by value
      .filter((opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i);
  } catch (err) {
    console.error("[locationApi] search failed:", err);
    return [];
  }
};

/**
 * Convenience loaders – each returns a function compatible with
 * AsyncSelect's `loadOptions` prop (receives inputValue, returns Promise<options>).
 */
export const loadStateOptions = (inputValue) =>
  searchLocations(inputValue, "state", 10);

export const loadCityOptions = (inputValue) =>
  searchLocations(inputValue, "city", 10);

// Districts are resolved via the "city" type (districts are administrative
// subdivisions at the same level the API uses for city searches).
export const loadDistrictOptions = (inputValue) =>
  searchLocations(inputValue, "city", 10);

export const loadPincodeOptions = (inputValue) =>
  searchLocations(inputValue, "pincode", 10);

/**
 * Reverse geocode a GPS coordinate into address components.
 *
 * @returns {Promise<object|null>} raw data object from API, or null on error
 */
export const reverseGeocode = async (lat, lng) => {
  const base = getBase();
  if (!base) return null;
  try {
    const res = await axios.get(`${base}/api/v2/location/reverse`, {
      params: { lat, lng },
    });
    if (res.data?.status === "success") return res.data?.data || null;
    return null;
  } catch (err) {
    console.error("[locationApi] reverseGeocode failed:", err);
    return null;
  }
};
